import React, { useState } from 'react';
import { Button, Input } from '@/components/ui';
import { Video, Plus, ArrowRight, Monitor, Users, Zap } from 'lucide-react';
import { apiClient } from '@/services/api';
import { useNavigate } from 'react-router-dom';

export const Home = () => {
  const [meetingTitle, setMeetingTitle] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
 
  const handleCreateMeeting = async () => {
    setIsCreating(true);
    setError('');

    try {
      const response = await apiClient.createMeeting({
        title: meetingTitle || 'Quick Meeting',
        createdBy: creatorName|| 'Guest'
      });

      if (response.success && response.data) {
        navigate(`/meeting/${response.data.meetingId}?name=${encodeURIComponent(creatorName)}`);
      }
    } catch (err) {
      setError('Failed to create meeting. Please try again.');
      console.error(err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinMeeting = () => {
    console.log('Navigate to join page');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-neutral-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-primary-400 to-primary-500 rounded-xl flex items-center justify-center">
                <Monitor className="text-white" size={20} />
              </div>
              <span className="text-xl font-semibold tracking-tight">Miiglu Meet</span>
            </div>
            <button 
              onClick={handleJoinMeeting}
              className="hidden sm:flex px-4 py-2 border border-white/10 hover:border-primary-400/30 hover:bg-white/5 rounded-lg transition-all"
            >
              Join Meeting
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center justify-between">
            
            {/* Left Content */}
            <div className="max-w-2xl">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-primary-400/20 mb-8">
                <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-pulse" />
                <span className="text-sm text-primary-400 font-medium">Next-Gen Collaboration</span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.1] mb-6">
                <span className="block text-white">Litemode<br/> Multi-Screen</span>
                <span className="block bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 bg-clip-text text-transparent">
                  Collaboration
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-lg sm:text-xl text-neutral-400 leading-relaxed mb-10 max-w-xl">
                Share up to 4 screens simultaneously. Perfect for design reviews, code comparisons, and collaborative workflows.
              </p>

              {/* Feature List */}
              <div className="space-y-4 mb-12">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/20">
                    <Monitor size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white mb-1">4 Concurrent Screens</h3>
                    <p className="text-sm text-neutral-400">Share multiple screens at once for seamless comparison</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/20">
                    <Users size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white mb-1">100+ Participants</h3>
                    <p className="text-sm text-neutral-400">Enterprise-scale meetings with crystal clear quality</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/20">
                    <Zap size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white mb-1">Real-Time Sync</h3>
                    <p className="text-sm text-neutral-400">Sub-50ms latency for smooth collaboration</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Card - Create Meeting */}
            <div className="w-full max-w-md mx-auto lg:mx-0">
              <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
                <h2 className="text-2xl font-semibold text-white mb-2">
                  Start a Meeting <span className='text-neutral-400 font-semibold text-sm'>(free for 30min before upgrade)</span>
                </h2>
                <p className="text-neutral-400 text-sm mb-8">
                  Create your meeting room in seconds
                </p>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your name"
                      value={creatorName}
                      onChange={(e) => setCreatorName(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400/50 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Meeting Title <span className="text-neutral-500">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Design Review Session"
                      value={meetingTitle}
                      onChange={(e) => setMeetingTitle(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-orange-400/50 focus:border-orange-400/50 transition-all"
                    />
                  </div>

                  {error && (
                    <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                      <p className="text-sm text-red-400">{error}</p>
                    </div>
                  )}

                  <button
                    onClick={handleCreateMeeting}
                    disabled={isCreating}
                    className="w-full px-6 py-3.5 bg-gradient-to-r from-primary-400 to-primary-500 hover:from-primary-500 hover:to-primary-600 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreating ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Plus size={20} />
                        Create Meeting
                      </>
                    )}
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/10" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-neutral-950 text-neutral-500">or</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/join')}
                    className="w-full px-6 py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-primary-400/30 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    Join Existing Meeting
                    <ArrowRight size={20} />
                  </button>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 flex items-center justify-center gap-6 text-sm text-neutral-500">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-accent-400 rounded-full" />
                  <span>Free to use</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-primary-400 rounded-full" />
                  <span>No signup required</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-500 rounded-lg flex items-center justify-center">
                <Monitor className="text-white" size={16} />
              </div>
              <span className="text-sm text-neutral-400">© 2025 Miiglu Meet</span>
            </div>
            <div className="text-sm text-neutral-500">
              Built for creative teams
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};